import { Clapperboard, Images, Users } from "lucide-react";
import type { Me, Member, Trip } from "../lib/api.ts";
import { Link, tripPath } from "../lib/router.tsx";
import { formatBytes } from "../lib/transfer.ts";
import { useApi } from "../lib/useApi.ts";
import { Card, ICON, Metric, NestedCard, PageState, ProgressBar } from "../ui/index.ts";
import { ActivityList, type ActivityItem } from "./ActivityPage.tsx";
import { summarize, type SyncStatusDto } from "./PeoplePage.tsx";

type DeliverableLite = { id: string; title: string; status: string };

/** C. Trip overview: capture and production as two parallel cards, then recent activity. */
export function OverviewPage({ trip }: { trip: Trip; me: Me }) {
  const sync = useApi<SyncStatusDto>(`/v1/trips/${trip.id}/sync-status`, { pollMs: 30_000 });
  const members = useApi<{ items: Member[] }>(`/v1/trips/${trip.id}/members`);
  const deliverables = useApi<{ items: DeliverableLite[] }>(`/v1/trips/${trip.id}/deliverables`);
  const activity = useApi<{ items: ActivityItem[] }>(`/v1/trips/${trip.id}/activity?limit=6`);

  return (
    <div className="stack">
      <div className="grid-2">
        <Card
          title="Capture"
          actions={
            <Link className="button small" to={tripPath(trip.id, "people")}>
              <Users {...ICON} aria-hidden="true" />
              People
            </Link>
          }
        >
          <PageState
            data={sync.data && members.data ? { s: sync.data, m: members.data.items } : null}
            error={sync.error ?? members.error}
            loading={sync.loading || members.loading}
            reload={() => void sync.reload()}
            skeletonRows={1}
          >
            {({ s, m }) => {
              const rows = summarize(m, s);
              const notScanned = rows.filter((r) => !r.scan).length;
              const discovered = Number(s.totals.discovered);
              const verified = Number(s.totals.verified);
              return (
                <>
                  <div className="metrics">
                    <Metric value={formatBytes(verified)} caption="Verified" />
                    <Metric value={formatBytes(Number(s.totals.pending))} caption="Pending" />
                  </div>
                  <ProgressBar
                    value={discovered > 0 ? verified / discovered : null}
                    label="Verified of discovered footage"
                    tone="good"
                  />
                  <NestedCard>
                    <span className="small">
                      {rows.length - notScanned} of {rows.length} contributors scanned
                      {notScanned > 0 ? ` · ${notScanned} not scanned` : ""}
                    </span>
                  </NestedCard>
                </>
              );
            }}
          </PageState>
        </Card>
        <Card
          title="Production"
          actions={
            <Link className="button small" to={tripPath(trip.id, "deliverables")}>
              <Clapperboard {...ICON} aria-hidden="true" />
              Deliverables
            </Link>
          }
        >
          <PageState {...deliverables} skeletonRows={1}>
            {(d) => {
              const by = (s: string) => d.items.filter((x) => x.status === s).length;
              return (
                <>
                  <div className="metrics">
                    <Metric value={String(d.items.length)} caption="Deliverables" />
                    <Metric value={String(by("APPROVED") + by("DELIVERED"))} caption="Approved" />
                  </div>
                  <NestedCard>
                    <span className="small">{by("IN_REVIEW")} awaiting review</span>
                  </NestedCard>
                </>
              );
            }}
          </PageState>
        </Card>
      </div>
      <Card
        title="Recent activity"
        actions={
          <Link className="button small" to={tripPath(trip.id, "media")}>
            <Images {...ICON} aria-hidden="true" />
            Media
          </Link>
        }
      >
        <PageState
          {...activity}
          skeletonRows={2}
          isEmpty={(d) => d.items.length === 0}
          empty={<p className="muted small">Nothing yet.</p>}
        >
          {(d) => <ActivityList items={d.items} />}
        </PageState>
      </Card>
    </div>
  );
}
